/**
 * Which brand this tab is serving.
 *
 * In Canada we operate as Clu, at app.clugtm.com. Everywhere else it is
 * Cleanlist, at app.cleanlist.ai. One build serves both domains, so the brand
 * is read from the hostname once, here, and copy reads it from `BRAND`.
 * On app.cleanlist.ai (and localhost, staging, previews, tests) every value is
 * exactly what the copy said before, so nothing changes there.
 *
 * Local preview: open http://app.clugtm.localhost:3000 to see the Clu brand.
 *
 * Sign-in is the exception to "read from the hostname". app.clugtm.com is a
 * Clerk satellite and can't host sign-in, so Canadians sign in on
 * app.cleanlist.ai/auth/*. Those pages show Clu when the visitor belongs to it
 * (`cluSignIn`), and nothing else on app.cleanlist.ai changes.
 *
 * Not branded on purpose, because they name things outside this app that keep
 * the Cleanlist name: the CRM fields the integration creates (Cleanlist ID,
 * Cleanlist_List__c), the G2 listing, support@cleanlist.ai, docs.cleanlist.ai,
 * the MCP URL and the Chrome extension's own panel (src/plugin), which only
 * runs on app.cleanlist.ai. Stripe plan names stay the keys `lib/credits.ts`
 * looks credits up by; `brandPlanName` only changes how they are shown.
 */
export type Brand = {
  id: 'cleanlist' | 'clu';
  /** The name in running copy: "Cleanlist" / "Clu". */
  name: string;
  /** Where copy said "Cleanlist AI": "Cleanlist AI" / "Clu". */
  fullName: string;
  /** Where copy said "Cleanlist.ai", and the tab title: "Cleanlist.ai" / "Clu". */
  dotName: string;
  /** The Slack app's name: "Clu by Cleanlist AI" / "Clu". */
  slackAppName: string;
  /** Marketing site, for terms, privacy and booking links. */
  site: string;
  /** The app's own host, where copy shows it. */
  appHost: string;
  /** Prefix for files people download. */
  fileSlug: string;
};

export const CLEANLIST_BRAND: Brand = {
  id: 'cleanlist',
  name: 'Cleanlist',
  fullName: 'Cleanlist AI',
  dotName: 'Cleanlist.ai',
  slackAppName: 'Clu by Cleanlist AI',
  site: 'https://www.cleanlist.ai',
  appHost: 'app.cleanlist.ai',
  fileSlug: 'cleanlist',
};

export const CLU_BRAND: Brand = {
  id: 'clu',
  name: 'Clu',
  fullName: 'Clu',
  dotName: 'Clu',
  slackAppName: 'Clu',
  site: 'https://clugtm.com',
  appHost: 'app.clugtm.com',
  fileSlug: 'clugtm',
};

export const isCluHost = (hostname: string): boolean =>
  /(^|\.)clugtm\.(com|localhost)$/.test(hostname);

export const brandFor = (hostname: string): Brand =>
  isCluHost(hostname) ? CLU_BRAND : CLEANLIST_BRAND;

/** The `<meta name="cl-brand" content="clu">` that marks auth-clu.html. */
export const CLU_PAGE_META = 'cl-brand';
/** Set once a sign-in visit is known to be Clu's, for the rest of the tab. */
export const CLU_SIGN_IN_KEY = 'cl-clu-sign-in';

/**
 * True when Clerk's `redirect_url` (in the query, or in the hash where Clerk
 * puts it: /auth/login#/?redirect_url=…) sends the user back to a Clu host.
 * That is the satellite handing sign-in off to app.cleanlist.ai.
 */
export const returnsToClu = (search: string, hash: string): boolean => {
  for (const part of [search, hash.slice(hash.indexOf('?') + 1)]) {
    const target = new URLSearchParams(part.replace(/^\?/, '')).get(
      'redirect_url'
    );
    if (!target) continue;
    try {
      if (isCluHost(new URL(target).hostname)) return true;
    } catch {
      // Not an absolute URL: not a satellite.
    }
  }
  return false;
};

export type SignInSignals = {
  pathname: string;
  search: string;
  hash: string;
  /** The page carries the Clu marker: Netlify served auth-clu.html (Country=ca). */
  markedClu: boolean;
  /** An earlier page in this tab was a Clu sign-in. */
  remembered: boolean;
};

/**
 * Whether a sign-in page on app.cleanlist.ai is Clu's. Only /auth/* pages:
 * once signed in, Canadians are sent on to app.clugtm.com, so app.cleanlist.ai
 * itself stays Cleanlist.
 */
export const cluSignIn = ({
  pathname,
  search,
  hash,
  markedClu,
  remembered,
}: SignInSignals): boolean =>
  /^\/auth(\/|$)/.test(pathname) &&
  (markedClu || remembered || returnsToClu(search, hash));

const readBrand = (): Brand => {
  if (typeof window === 'undefined') return CLEANLIST_BRAND;
  const { hostname, pathname, search, hash } = window.location;
  if (isCluHost(hostname)) return CLU_BRAND;
  let remembered = false;
  try {
    remembered = sessionStorage.getItem(CLU_SIGN_IN_KEY) === '1';
  } catch {
    // Storage blocked: the other signals still apply.
  }
  const markedClu =
    document
      .querySelector(`meta[name="${CLU_PAGE_META}"]`)
      ?.getAttribute('content') === 'clu';
  if (!cluSignIn({ pathname, search, hash, markedClu, remembered })) {
    return CLEANLIST_BRAND;
  }
  // Google and Microsoft sign-in leave the app and come back to
  // /auth/sso-callback without the redirect_url: remember the brand.
  try {
    sessionStorage.setItem(CLU_SIGN_IN_KEY, '1');
  } catch {
    // Storage blocked: the page still shows Clu.
  }
  // index.html names the tab Clu only for the marked page; the sign-in pages
  // set no title of their own.
  document.title = CLU_BRAND.dotName;
  return CLU_BRAND;
};

export const BRAND: Brand = readBrand();

/** Public files that show the Cleanlist name, with a Clu copy under public/clu/. */
const CLU_ASSETS = new Set([
  '/logo-dark.png',
  '/logo-light.png',
  '/appsumo/clu-champion-agent.jpg',
  '/appsumo/clu-in-slack.jpg',
  '/appsumo/clu-launch.jpg',
  '/whats-new/clu-v1-poster.jpg',
  '/whats-new/sequences-v1-poster.jpg',
  '/cleanlist-import-template.csv',
]);

export const assetFor = (brand: Brand, path: string): string =>
  brand.id === 'clu' && CLU_ASSETS.has(path) ? `/clu${path}` : path;

/** A public file's path, swapped for its Clu copy on the Clu brand. */
export const brandAsset = (path: string): string => assetFor(BRAND, path);

export const siteUrlFor = (brand: Brand, url: string): string =>
  brand.id === 'clu'
    ? url.replace(/^https:\/\/(www\.)?cleanlist\.ai(?=\/|$)/, brand.site)
    : url;

/** A cleanlist.ai marketing link (terms, privacy, booking), sent to clugtm.com on the Clu brand. */
export const siteUrl = (url: string): string => siteUrlFor(BRAND, url);

export const planNameFor = <T extends string | null | undefined>(
  brand: Brand,
  name: T
): T =>
  (typeof name === 'string'
    ? name.replace(/^Cleanlist(?= )/, brand.name)
    : name) as T;

/**
 * A Stripe plan name ("Cleanlist Pro (5,000)") as the brand shows it. Display
 * only: code that looks plans up keeps using the Stripe name.
 */
export const brandPlanName = <T extends string | null | undefined>(
  name: T
): T => planNameFor(BRAND, name);
